import type { Yo } from '../api/operaciones';
import { MENSAJES, emailValido } from './acceso';

/**
 * Reglas del alta de un propietario nuevo (`diseno/plataforma/1.1-1.6-1.10-alta-e-inicio-de-sesion.html`, tarea 490):
 * «Crear cuenta» (1.1, `signup1`) → «Revisa tu correo» y «Email verificado» (1.2) → «Protege tu cuenta» (1.6, `offer2fa` y
 * `twofaSetup`, sin obligar, G65) → «Tu negocio» (1.5, `signup2`) → el panel, con «Paso N de 4» como el dibujo. Sin React,
 * para probarlas solas. Las reglas de verdad (contraseña filtrada, email ya usado) las aplica la API.
 */

/** «Paso N de 4» del alta (`STEPS` del dibujo): 1.2 es el 2, 1.6 el 3 y 1.5 el 4; «Crear cuenta» no lo lleva. */
export const PASOS_ALTA = { verificar: 2, proteger: 3, negocio: 4 } as const;
export const TOTAL_PASOS_ALTA = 4;

export const textoPaso = (paso: number): string => `Paso ${String(paso)} de ${String(TOTAL_PASOS_ALTA)}`;

export const TEXTOS_CUENTA = {
    crear: 'Crear cuenta',
    comprobando: 'Comprobando…',
    nombre: 'Escribe tu nombre y tus apellidos.',
    robot: 'No hemos podido comprobar que no eres un robot. Vuelve a intentarlo.',
} as const;

/** Textos de «Protege tu cuenta» (1.6), tal cual del dibujo; los códigos de recuperación, los de «Mi cuenta» (1.8). */
export const TEXTOS_PROTEGER = {
    titulo: 'Protege tu cuenta',
    sub: 'Además de la contraseña, te pediremos un código del móvil. Es opcional.',
    /** Tras aceptar una invitación con una cuenta nueva (1.10). */
    subInvitacion: (negocio: string) => `Ya formas parte de ${negocio}. Además de la contraseña, te pediremos un código del móvil. Es opcional.`,
    activarAhora: 'Activarla ahora',
    preparando: 'Preparando…',
    ahoraNo: 'Ahora no',
    activaTitulo: 'Activa los dos pasos',
    activaSub: 'Escanéalo con tu app de autenticación y escribe los 6 números.',
    activar: 'Activar',
    noPuedes: '¿No puedes escanearlo?',
    verClave: 'Ver la clave',
    codigosTitulo: 'Tus códigos de recuperación',
    codigosSub: 'Guárdalos en un sitio seguro. Cada uno sirve una vez y no los volverás a ver.',
    guardados: 'Ya los he guardado',
    copiar: 'Copiar',
    copiados: 'Copiados',
} as const;

export interface ErroresCuenta {
    nombre?: string;
    email?: string;
    password?: string;
}

/** Lo que se ve sin preguntar a la API: nombre, email con forma de email y contraseña de 12 como mínimo (G64). */
export function validarCuenta(nombre: string, email: string, password: string): ErroresCuenta {
    const e: ErroresCuenta = {};
    if (nombre.trim() === '') {
        e.nombre = TEXTOS_CUENTA.nombre;
    }
    if (!emailValido(email)) {
        e.email = MENSAJES.emailNoValido;
    }
    if (password.length < 12) {
        e.password = MENSAJES.minimo12;
    }
    return e;
}

/** ¿Está la cuenta en su alta (1.1 → 1.5)? Una cuenta del equipo de un negocio que aún no trabaja en ninguno. */
export function enAlta(yo: Yo | null): boolean {
    return yo !== null && yo.account.type === 'staff' && yo.memberships.length === 0;
}

/**
 * Tras «Email verificado» en el alta: «Protege tu cuenta» (1.6) si aún no tiene los dos pasos; si ya los tiene, directo a
 * «Tu negocio» (1.5). Fuera del alta, null (sigue como siempre).
 */
export function siguienteTrasVerificar(yo: Yo | null): 'offer2fa' | 'newbiz' | null {
    if (!enAlta(yo) || yo === null) {
        return null;
    }
    return yo.account.two_factor_enabled ? 'newbiz' : 'offer2fa';
}

/** La clave del alta de los dos pasos en grupos de cuatro, para copiarla a mano. */
export const claveEnGrupos = (secreto: string): string => secreto.replace(/(.{4})/g, '$1 ').trim();
